import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { App } from '@capacitor/app';
import { Photo } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { Platform } from '@ionic/angular';
import { environment } from '../environments/environment';
import { Evaluation } from './models/interfaces/evaluation';
import { FileType } from './shared/model/file.type';
import { PendingPhotoCapture } from './shared/model/session-recovery.model';
import { ActiveNavigationService } from './shared/services/active-navigation.service';
import { AppUpdateService } from './shared/services/app-update.service';
import { SentryService } from './shared/services/sentry.service';
import { AuthService } from './shared/services/auth.service';
import { NewEvaluationService } from './shared/services/new-evaluation.service';
import { PendingPhotoService } from './shared/services/pending-photo.service';
import { PhotoService } from './shared/services/photo.service';
import { PreloaderService } from './shared/services/preloader.service';
import { PwaUpdateService } from './shared/services/pwa-update.service';
import { StorageService } from './shared/services/storage.service';


@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  private restoredPhotoResult: any = null;

  constructor(
    private authService: AuthService,
    private router: Router,
    private storage: StorageService,
    private preloadService: PreloaderService,
    private platform: Platform,
    private appUpdateService: AppUpdateService,
    private pwaUpdateService: PwaUpdateService,
    private sentryService: SentryService,
    private pendingPhotoService: PendingPhotoService,
    private activeNavigationService: ActiveNavigationService,
    private photoService: PhotoService,
    private evaluationService: NewEvaluationService
  ) {

  }


  async ngOnInit() {
    try {
      await this.platform.ready();

      // KILL SWITCH — solo web. Nunca corre en Android/iOS ni en desarrollo.
      if (environment.killSwitch && Capacitor.getPlatform() === 'web') {
        await this.pwaUpdateService.killSwitch();
        await SplashScreen.hide();
        return;
      }

      await SplashScreen.show({
        autoHide: false,
      });

      // Registrar listener temprano para capturar resultados restaurados de plugins
      this.setupAppStateListeners();

      // SafeArea plugin removido - ahora se usa edge-to-edge nativo
      // Configurado en MainActivity.java y styles.xml
      // Ionic maneja los safe area insets automáticamente

      // Esperar a que storage se inicialice antes de continuar
      // Con timeout de seguridad para evitar bloqueos
      await this.initializeStorageWithTimeout();
      // IMPORTANTE: cargar evaluaciones ANTES de restaurar sesión.
      // Si checkPrevSession() corre primero, reloadEvaluationsForCurrentUser()
      // encuentra evaluations$.value === null y emite [], causando que
      // los componentes no encuentren sus evaluaciones tras un reload.
      await this.preloadService.init();
      await this.authService.checkPrevSession();

      // Inicializar servicio de actualizaciones PWA
      this.pwaUpdateService.init();

      // Reload PWA diferido: aplicar actualización al llegar a /home o /login
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe((e) => {
          this.pwaUpdateService.applyUpdateIfAtSafePage(e.url);
          this.activeNavigationService.trackNavigation(e.url);
        });

      // Restaurar foto capturada interrumpida (Android) y navegación activa (Android + iOS)
      await this.processRestoredPhotoIfAny();
      const restored = await this.restoreActiveNavigation();
      if (!restored) {
        await this.checkIfLogged();
      }

      // Verificar actualizaciones disponibles en Play Store
      // Se ejecuta después de inicializar la app
      setTimeout(() => {
        this.appUpdateService.checkForUpdates();
      }, 3000); // Delay de 3 segundos para no interferir con la carga inicial

    } catch (error) {
      console.error('Error inicializando app:', error);

      // Reportar error a Sentry
      if (error instanceof Error) {
        this.sentryService.captureException(error, {
          tags: { component: 'AppComponent', method: 'ngOnInit' },
          extra: {
            errorType: 'initialization_error',
            errorMessage: error.message,
            errorStack: error.stack
          }
        });
      }

      // Navegación de fallback para evitar pantalla en blanco
      // Intentar navegar a login como última opción
      try {
        await this.router.navigateByUrl('/login');
      } catch (navError) {
        console.error('Error crítico: no se pudo navegar a login', navError);
        // Si incluso la navegación falla, recargar la app
        window.location.href = '/login';
      }
    } finally {
      // Esperar a que los safe-area insets estén disponibles antes del
      // primer frame, para que el footer no se pinte pegado al borde.
      await this.waitForSafeAreaInsets();
      // Asegurar que SplashScreen se oculte siempre
      await SplashScreen.hide();
    }
  }

  /**
   * Inicializa el storage con timeout de seguridad
   * Previene bloqueos indefinidos si el storage falla
   */
  private async initializeStorageWithTimeout(): Promise<void> {
    const STORAGE_TIMEOUT = 3000; // 3 segundos

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error('Storage initialization timeout after 3 seconds'));
      }, STORAGE_TIMEOUT);
    });

    try {
      await Promise.race([
        this.storage.init(),
        timeoutPromise
      ]);
      console.log('✅ Storage inicializado correctamente');
    } catch (error) {
      console.error('❌ Error inicializando storage:', error);
      throw error;
    }
  }

  /**
   * Espera a que Capacitor inyecte la variable CSS --safe-area-inset-bottom.
   * En Android WebView < 140, env(safe-area-inset-bottom) devuelve 0 en el
   * primer render; Capacitor (@capacitor/status-bar) inyecta la variable con
   * el valor correcto de forma asíncrona. Esperamos a que esté inyectada
   * (aunque sea "0px") para pintar el primer frame con el safe-area correcto.
   */
  private async waitForSafeAreaInsets(timeoutMs = 1500): Promise<void> {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      const bottom = document.documentElement.style.getPropertyValue(
        '--safe-area-inset-bottom'
      );
      if (bottom) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }

  async checkIfLogged() {
    const token = await this.authService.token();
    if (token) {
      this.router.navigateByUrl('home');
    }
  }

  private setupAppStateListeners(): void {
    App.addListener('appRestoredResult', (data) => {
      if (
        data?.pluginId === 'Camera' &&
        data?.methodName === 'getPhoto' &&
        data?.success === true
      ) {
        this.restoredPhotoResult = data;
      }
    });
  }

  private async processRestoredPhotoIfAny(): Promise<void> {
    try {
      const pending = await this.pendingPhotoService.getPendingCapture();
      if (!pending) {
        return;
      }

      if (!this.restoredPhotoResult) {
        // No llegó resultado restaurado → descartar metadata huérfana
        await this.pendingPhotoService.clearPendingCapture();
        return;
      }

      const result = await this.photoService.processCapturedPhoto(
        this.restoredPhotoResult.data as Photo,
        pending.fileType
      );

      if (!result) {
        await this.pendingPhotoService.clearPendingCapture();
        return;
      }

      await this.associateRecoveredPhoto(pending, result.uri);
      await this.pendingPhotoService.clearPendingCapture();
      console.log('✅ Foto recuperada automáticamente:', result.uri);
    } catch (error) {
      console.error('Error recuperando foto restaurada:', error);
      this.sentryService.captureException(error, {
        extra: { errorType: 'app_restored_result_failed' },
      });
      await this.pendingPhotoService.clearPendingCapture();
    }
  }

  private async associateRecoveredPhoto(
    pending: PendingPhotoCapture,
    uri: string
  ): Promise<void> {
    switch (pending.fileType) {
      case FileType.evidence:
        this.evaluationService.setEvidencePhoto(
          pending.evaluationHash,
          pending.evidenceIndex,
          uri
        );
        break;
      case FileType.category:
        if (pending.collagePosition !== undefined) {
          this.evaluationService.saveCollagePhoto(
            pending.evaluationHash,
            pending.categoryPosition,
            pending.collagePosition,
            uri
          );
        } else {
          this.evaluationService.saveCategoryPhoto(
            pending.evaluationHash,
            pending.categoryPosition,
            pending.categoryEvidenceIndex,
            pending.imageType === 'before' ? 'image_before' : 'image_after',
            uri
          );
        }
        break;
      case FileType.exhibition:
        this.evaluationService.setExhibitionPhoto(
          pending.evaluationHash,
          pending.exhibitionIndex,
          uri
        );
        break;
      default:
        break;
    }
  }

  private async restoreActiveNavigation(): Promise<boolean> {
    try {
      const navigation = await this.activeNavigationService.getActiveNavigation();
      if (!navigation) {
        return false;
      }

      const token = await this.authService.token();
      if (!token) {
        return false;
      }

      const evaluation = this.evaluationService.findEvaluationByHash(
        navigation.hash
      );
      if (!evaluation || evaluation.finished_at) {
        await this.activeNavigationService.clearActiveNavigation();
        return false;
      }

      const targetUrl = this.isSubRouteValid(navigation.url, evaluation)
        ? navigation.url
        : `stablishment/${navigation.hash}`;
      await this.router.navigateByUrl(targetUrl, { replaceUrl: true });
      return true;
    } catch (error) {
      console.error('Error restaurando navegación:', error);
      this.sentryService.captureException(error, {
        extra: { errorType: 'navigation_restore_failed' },
      });
      return false;
    }
  }

  private isSubRouteValid(url: string, evaluation: Evaluation): boolean {
    const segments = url.split('/');
    if (segments[0] === '') {
      segments.shift();
    }
    // segments: ['stablishment', hash, ...]
    if (segments.length <= 2) {
      return true; // solo 'stablishment/{hash}'
    }

    const categoryIdx = segments.indexOf('category');
    if (categoryIdx >= 0 && segments.length > categoryIdx + 1) {
      const pos = Number(segments[categoryIdx + 1]);
      if (!evaluation.categories || !evaluation.categories[pos]) {
        return false;
      }
      const category = evaluation.categories[pos];

      const productIdx = segments.indexOf('product');
      if (productIdx >= 0 && segments.length > productIdx + 1) {
        const pPos = Number(segments[productIdx + 1]);
        if (!category.products || !category.products[pPos]) {
          return false;
        }
      }

      const subIdx = segments.indexOf('subcategory');
      if (subIdx >= 0 && segments.length > subIdx + 1) {
        const sPos = Number(segments[subIdx + 1]);
        if (!category.sub_categories || !category.sub_categories[sPos]) {
          return false;
        }
      }
    }

    return true;
  }
}
